// Copyright © 2026 Gian Marco Cappelli — SAPIENS (finsapiens.it). Tutti i diritti riservati. Sviluppato su commissione esclusiva. Vietata la riproduzione senza autorizzazione scritta.
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../App';
import { motion, AnimatePresence } from 'framer-motion';
import { LevelBadge } from '../components/UIComponents';
import { Button } from '../components/ui/button';
import { Clock, ChevronLeft, ChevronRight, CheckCircle, Lock, Award } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Inline Mini-Quiz ────────────────────────────────────────────────────────
const MiniQuiz = ({ questions, onComplete }) => {
  const [selected, setSelected] = useState(new Array(questions.length).fill(null));
  const [submitted, setSubmitted] = useState(false);

  const handleSelect = (qIdx, optIdx) => {
    if (submitted) return;
    const next = [...selected];
    next[qIdx] = optIdx;
    setSelected(next);
  };

  const handleSubmit = () => {
    if (selected.includes(null)) { toast.error('Rispondi a tutte le domande prima di continuare'); return; }
    setSubmitted(true);
  };

  const score = submitted ? selected.filter((ans, i) => ans === questions[i].correct_index).length : 0;
  const allAnswered = !selected.includes(null);

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="sapiens-card p-6 mb-6" data-testid="mini-quiz-section">
      <div className="flex items-center gap-3 mb-5">
        <div className="w-8 h-8 rounded-full bg-[#C9A84C]/10 flex items-center justify-center shrink-0">
          <Award className="w-4 h-4 text-[#C9A84C]" />
        </div>
        <div>
          <h3 className="text-[#E8E8E4] font-semibold text-base">Metti alla prova le tue conoscenze</h3>
          <p className="text-xs text-[#888780]">{questions.length} domande · Completa prima di andare avanti</p>
        </div>
      </div>

      <div className="space-y-6">
        {questions.map((q, qi) => (
          <div key={qi} data-testid={`mini-quiz-q-${qi}`}>
            <p className="text-sm font-medium text-[#E8E8E4] mb-3">{qi + 1}. {q.question}</p>
            <div className="space-y-2">
              {q.options.map((opt, oi) => {
                const isSelected = selected[qi] === oi;
                const isCorrect = submitted && oi === q.correct_index;
                const isWrong = submitted && isSelected && oi !== q.correct_index;
                return (
                  <button
                    key={oi}
                    onClick={() => handleSelect(qi, oi)}
                    disabled={submitted}
                    data-testid={`mini-quiz-q${qi}-opt${oi}`}
                    className={`w-full text-left px-4 py-2.5 rounded-xl text-sm transition-all border ${
                      isCorrect ? 'border-[#3D7A52] bg-[#3D7A52]/10 text-[#3D7A52] font-medium'
                      : isWrong ? 'border-[#F09595] bg-[#F09595]/10 text-[#F09595]'
                      : isSelected ? 'border-[#C9A84C] bg-[#C9A84C]/10 text-[#E8E8E4]'
                      : 'border-white/[0.08] bg-[#252523] text-[#888780] hover:border-white/[0.18] hover:text-[#E8E8E4]'
                    }`}
                  >
                    {opt}
                    {isCorrect && <span className="ml-2">✓</span>}
                    {isWrong && <span className="ml-2">✗</span>}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {!submitted ? (
        <button
          onClick={handleSubmit}
          disabled={!allAnswered}
          data-testid="mini-quiz-submit-btn"
          className={`mt-5 w-full py-3 rounded-xl text-sm font-semibold transition-all ${allAnswered ? 'bg-[#C9A84C] text-[#3A2800] hover:opacity-90' : 'bg-white/[0.04] text-[#888780] cursor-not-allowed'}`}
        >
          Verifica le risposte
        </button>
      ) : (
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-5" data-testid="mini-quiz-result">
          <div className={`flex items-center justify-between px-4 py-3 rounded-xl mb-4 ${score === questions.length ? 'bg-[#3D7A52]/10 border border-[#3D7A52]/30' : score >= Math.ceil(questions.length / 2) ? 'bg-[#C9A84C]/10 border border-[#C9A84C]/30' : 'bg-white/[0.04] border border-white/[0.08]'}`}>
            <span className="text-sm font-medium text-[#E8E8E4]">
              {score === questions.length ? '🎉 Perfetto!' : score >= Math.ceil(questions.length / 2) ? '👍 Buon lavoro!' : '📚 Rileggi e riprova!'}
            </span>
            <span className="text-lg font-bold" style={{ color: score === questions.length ? '#3D7A52' : score >= Math.ceil(questions.length / 2) ? '#C9A84C' : '#888780' }}>
              {score}/{questions.length}
            </span>
          </div>
          <button onClick={onComplete} data-testid="mini-quiz-continue-btn" className="w-full py-3 rounded-xl text-sm font-semibold bg-[#C9A84C] text-[#3A2800] hover:opacity-90 transition-all">
            Continua →
          </button>
        </motion.div>
      )}
    </motion.div>
  );
};

// ── Main LessonPage ─────────────────────────────────────────────────────────
const LessonPage = () => {
  const { lessonId } = useParams();
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [lesson, setLesson] = useState(null);
  const [allLessons, setAllLessons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [completing, setCompleting] = useState(false);
  const [quizDone, setQuizDone] = useState(false);

  useEffect(() => {
    setQuizDone(false);
    const fetchData = async () => {
      setLoading(true);
      try {
        const [lessonRes, allLessonsRes] = await Promise.all([
          fetch(`${API_URL}/api/lessons/${lessonId}`, { credentials: 'include' }),
          fetch(`${API_URL}/api/lessons`, { credentials: 'include' })
        ]);
        if (lessonRes.ok) setLesson(await lessonRes.json());
        else if (lessonRes.status === 404) navigate('/percorsi');
        if (allLessonsRes.ok) setAllLessons(await allLessonsRes.json());
      } catch (error) { console.error('Error fetching lesson:', error); }
      finally { setLoading(false); }
    };
    fetchData();
  }, [lessonId, navigate]);

  const isLastLessonOfLevel = () => {
    if (!lesson || allLessons.length === 0) return false;
    const realLessons = allLessons.filter(l => !l.lesson_id.startsWith('quiz_'));
    const levelLessons = realLessons.filter(l => l.level === lesson.level);
    const maxOrder = Math.max(...levelLessons.map(l => l.order));
    return lesson.order === maxOrder;
  };

  const getNextLesson = () => {
    if (!lesson || allLessons.length === 0) return null;
    const realLessons = allLessons.filter(l => !l.lesson_id.startsWith('quiz_'));
    const sorted = [...realLessons].sort((a, b) => a.level !== b.level ? a.level - b.level : a.order - b.order);
    const currentIndex = sorted.findIndex(l => l.lesson_id === lessonId);
    if (currentIndex === -1 || currentIndex >= sorted.length - 1) return null;
    return sorted[currentIndex + 1];
  };

  const handleComplete = async () => {
    if (!user) { toast.error('Devi accedere per completare le lezioni'); navigate('/login'); return; }
    setCompleting(true);
    try {
      const res = await fetch(`${API_URL}/api/lessons/${lessonId}/complete`, { method: 'POST', credentials: 'include' });
      if (res.ok) {
        toast.success('Lezione completata! 🎉');
        refreshUser();
        if (isLastLessonOfLevel()) {
          navigate(`/quiz/quiz_l${lesson.level}`);
        } else {
          const next = getNextLesson();
          if (next) navigate(`/lezione/${next.lesson_id}`);
          else navigate('/percorsi');
        }
      }
    } catch { toast.error('Errore nel completamento della lezione'); }
    finally { setCompleting(false); }
  };

  const hasMiniQuiz = lesson?.mini_quiz_questions?.length > 0;
  const nextLesson = getNextLesson();

  const renderContent = (content) => {
    if (!content) return null;
    const lines = content.split('\n');
    const elements = [];
    let listItems = [];
    const flushList = () => {
      if (listItems.length > 0) {
        elements.push(<ul key={`list-${elements.length}`} className="list-disc pl-6 mb-4 space-y-2">{listItems.map((item, i) => <li key={i} className="text-[#E8E8E4]">{item}</li>)}</ul>);
        listItems = [];
      }
    };
    lines.forEach((line, index) => {
      if (line.startsWith('# ')) { flushList(); elements.push(<h1 key={index} className="text-3xl font-medium text-[#E8E8E4] mb-6 mt-8 first:mt-0">{line.slice(2)}</h1>); }
      else if (line.startsWith('## ')) { flushList(); elements.push(<h2 key={index} className="text-2xl font-medium text-[#E8E8E4] mb-4 mt-8">{line.slice(3)}</h2>); }
      else if (line.startsWith('### ')) { flushList(); elements.push(<h3 key={index} className="text-xl font-medium text-[#E8E8E4] mb-3 mt-6">{line.slice(4)}</h3>); }
      else if (line.startsWith('- ')) { const text = line.slice(2).replace(/\*\*(.*?)\*\*/g, '<strong class="text-[#C9A84C] font-medium">$1</strong>'); listItems.push(<span dangerouslySetInnerHTML={{ __html: text }} />); }
      else if (/^\d+\. /.test(line)) { flushList(); const text = line.replace(/^\d+\. /, '').replace(/\*\*(.*?)\*\*/g, '<strong class="text-[#C9A84C] font-medium">$1</strong>'); elements.push(<p key={index} className="mb-2 ml-6 text-[#E8E8E4]"><span className="text-[#3D7A52] mr-2 tabular-nums font-medium">{line.match(/^\d+/)[0]}.</span><span dangerouslySetInnerHTML={{ __html: text }} /></p>); }
      else if (line.trim() === '') { flushList(); }
      else { flushList(); const text = line.replace(/\*\*(.*?)\*\*/g, '<strong class="text-[#C9A84C] font-medium">$1</strong>'); elements.push(<p key={index} className="mb-4 leading-relaxed text-[#E8E8E4]" dangerouslySetInnerHTML={{ __html: text }} />); }
    });
    flushList();
    return elements;
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-[#1C1C1A]"><div className="w-12 h-12 border-4 border-[#3D7A52] border-t-transparent rounded-full animate-spin"></div></div>;

  if (!lesson) return (
    <div className="min-h-screen bg-[#1C1C1A] flex items-center justify-center px-4">
      <div className="text-center max-w-md">
        <div className="text-6xl mb-6">📚</div>
        <h2 className="text-2xl font-semibold text-[#E8E8E4] mb-3">Contenuto in arrivo</h2>
        <p className="text-[#888780] text-base leading-relaxed mb-8">Questa lezione sarà disponibile presto. Nel frattempo esplora i calcolatori o il glossario.</p>
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button onClick={() => navigate('/calcolatori')} className="sapiens-btn-primary px-6 py-3" data-testid="lesson-placeholder-calc-btn">Esplora i calcolatori →</button>
          <button onClick={() => navigate('/percorsi')} className="sapiens-btn-secondary px-6 py-3" data-testid="lesson-placeholder-back-btn">Torna ai percorsi</button>
        </div>
      </div>
    </div>
  );

  if (lesson.premium_required) {
    return (
      <div className="min-h-screen bg-[#1C1C1A] py-8" data-testid="lesson-page-locked">
        <div className="max-w-[680px] mx-auto px-4">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center py-20">
            <div className="w-24 h-24 bg-[#C9A84C]/10 rounded-full flex items-center justify-center mx-auto mb-6"><Lock className="w-12 h-12 text-[#C9A84C]" /></div>
            <h1 className="text-3xl font-medium text-[#E8E8E4] mb-4">{lesson.title}</h1>
            <LevelBadge level={lesson.level} />
            <p className="text-lg text-[#888780] mt-6 mb-8 max-w-md mx-auto">{lesson.content}</p>
            <Button className="sapiens-btn-primary px-8 py-3" onClick={() => navigate('/profilo')} data-testid="upgrade-from-lesson-btn">Passa a Premium</Button>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1C1C1A] py-8" data-testid="lesson-page">
      <div className="max-w-[680px] mx-auto px-4">
        <div className="flex items-center justify-between mb-8">
          <Button variant="ghost" onClick={() => navigate('/percorsi')} className="text-[#888780] hover:text-[#E8E8E4] -ml-4" data-testid="back-to-percorsi">
            <ChevronLeft className="w-4 h-4 mr-2" />Torna ai percorsi
          </Button>
          <div className="flex items-center gap-2 text-[#888780]"><Clock className="w-4 h-4" /><span className="text-sm">{lesson.estimated_time} min di lettura</span></div>
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-8">
          <LevelBadge level={lesson.level} />
        </motion.div>

        {/* Lesson Content */}
        <motion.article initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="sapiens-card p-8 mb-8">
          {renderContent(lesson.content)}
        </motion.article>

        {/* Mini Quiz — shown if lesson has mini_quiz_questions and not yet done */}
        <AnimatePresence>
          {hasMiniQuiz && !quizDone && (
            <MiniQuiz
              key={lessonId}
              questions={lesson.mini_quiz_questions}
              onComplete={() => setQuizDone(true)}
            />
          )}
        </AnimatePresence>

        {/* Complete Button — shown always (if no mini-quiz) OR after quiz done */}
        {(!hasMiniQuiz || quizDone) && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="sapiens-card p-6 mb-6">
            <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
              <div>
                <h3 className="text-[#E8E8E4] font-medium text-lg">
                  {isLastLessonOfLevel() ? `Fine del Livello ${lesson.level}!` : 'Hai completato la lezione?'}
                </h3>
                <p className="text-sm text-[#888780]">
                  {isLastLessonOfLevel()
                    ? `Completa e fai il quiz finale del Livello ${lesson.level} per sbloccare il prossimo!`
                    : 'Segna la lezione come completata e passa alla prossima'}
                </p>
              </div>
              <Button
                className="sapiens-btn-primary px-6 py-3 shrink-0"
                onClick={handleComplete}
                disabled={completing}
                data-testid="complete-lesson-btn"
              >
                {completing ? 'Caricamento...' : isLastLessonOfLevel()
                  ? <><Award className="w-5 h-5 mr-2" />Quiz finale del Livello {lesson.level} →</>
                  : <><CheckCircle className="w-5 h-5 mr-2" />Completa lezione</>
                }
              </Button>
            </div>
          </motion.div>
        )}

        {/* Next Lesson Preview — only if not last of level */}
        {nextLesson && !isLastLessonOfLevel() && (!hasMiniQuiz || quizDone) && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
            <Link to={`/lezione/${nextLesson.lesson_id}`} className="flex items-center justify-between p-6 sapiens-card border-[#C9A84C]/30 hover:border-[#C9A84C]/50 transition-colors group" data-testid="next-lesson-link">
              <div>
                <span className="text-sm text-[#888780]">Prossima lezione</span>
                <h3 className="text-[#E8E8E4] font-medium mt-1">{nextLesson.title}</h3>
                <div className="flex items-center gap-2 mt-2"><LevelBadge level={nextLesson.level} /><span className="text-sm text-[#888780]">{nextLesson.estimated_time} min</span></div>
              </div>
              <div className="flex items-center gap-2 text-[#C9A84C] group-hover:translate-x-1 transition-transform">
                <span className="font-medium">Vai</span><ChevronRight className="w-5 h-5" />
              </div>
            </Link>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default LessonPage;
